import React, { useMemo } from 'react';
import { Carousel, Empty, Spin } from 'antd';
import {
  apiV2EnvMonitorDashboardCardOverviewPost,
  type V2EnvMonitorDashboardCardOverviewPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import styles from './index.module.scss';

type IndicatorGroup = NonNullable<V2EnvMonitorDashboardCardOverviewPostResponse['exhaustIndicators']>;
type Indicator = NonNullable<IndicatorGroup[keyof IndicatorGroup]>;
type Period = 'month' | 'year';

interface CenterTopCardsProps {
  className?: string;
}

type MetricItem = {
  key: string;
  title: string;
  unit?: string;
  monthPlan?: number;
  monthActual?: number;
  monthRatio?: number;
  yearPlan?: number;
  yearActual?: number;
  yearRatio?: number;
};

const METRIC_LABELS = {
  exhaustVolume: '废气排放总量',
  noxEmission: '氮氧化物排放量',
  nmhcEmission: '非甲烷总烃排放量',
  ammoniaNitrogenEmission: '氨氮排放量',
  totalPhosphorusEmission: '总磷排放量',
  codEmission: 'COD排放量',
  totalNitrogenEmission: '总氮排放量',
  wastewaterVolume: '废水排放量',
} as const;

const formatValue = (value?: number) => {
  if (value === undefined || value === null) return '--';
  if (value >= 10000) return `${Math.round(value / 10000)}万`;
  return `${Math.round(value)}`;
};

const formatRatio = (value?: number) => {
  if (value === undefined || value === null) return '--';
  return `${value.toFixed(1)}%`;
};

const getRatioPercent = (value?: number) => {
  if (value === undefined || value === null) return 0;
  return Math.max(0, Math.min(100, Number(value) || 0));
};

const collectMetrics = (data?: V2EnvMonitorDashboardCardOverviewPostResponse): MetricItem[] => {
  const source = {
    ...data?.exhaustIndicators,
    ...data?.wastewaterIndicators,
  } as Record<string, Indicator | undefined>;

  return Object.entries(METRIC_LABELS)
    .map(([key, title]) => {
      const item = source[key];
      if (!item) return null;
      return {
        key,
        title,
        unit: item.unit,
        monthPlan: item.monthPlan,
        monthActual: item.monthActual,
        monthRatio: item.monthRatio,
        yearPlan: item.yearPlan,
        yearActual: item.yearActual,
        yearRatio: item.yearRatio,
      };
    })
    .filter(Boolean) as MetricItem[];
};

const getMetricValue = (metric: MetricItem, period: Period, type: 'plan' | 'actual' | 'ratio') => {
  const periodKey = period === 'month' ? 'month' : 'year';
  if (type === 'plan') return metric[`${periodKey}Plan`];
  if (type === 'actual') return metric[`${periodKey}Actual`];
  return metric[`${periodKey}Ratio`];
};

const MetricCard: React.FC<{ metric: MetricItem; period: Period }> = ({ metric, period }) => {
  const plan = getMetricValue(metric, period, 'plan');
  const actual = getMetricValue(metric, period, 'actual');
  const ratio = getMetricValue(metric, period, 'ratio');
  const ratioPercent = getRatioPercent(ratio);

  return (
    <div className={styles.metricCard}>
      <div className={styles.metricContent}>
        <div className={styles.metricTitle}>{metric.title}</div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>计划({metric.unit ?? '--'}):</span>
          <span className={styles.metricValue}>{formatValue(plan)}</span>
        </div>
        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>实际({metric.unit ?? '--'}):</span>
          <span className={styles.metricValue}>{formatValue(actual)}</span>
        </div>
      </div>
      <div className={styles.ratioWrap}>
        <div className={styles.ratioCircle} style={{ '--ratio-percent': ratioPercent } as React.CSSProperties} />
        <div className={styles.ratioText}>{formatRatio(ratio)}</div>
      </div>
    </div>
  );
};

const PeriodCard: React.FC<{ title: string; period: Period; metrics: MetricItem[]; loading: boolean }> = ({
  title,
  period,
  metrics,
  loading,
}) => {
  const renderCards = () => {
    if (metrics.length === 0) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} className={styles.empty} />;
    }

    if (metrics.length > 3) {
      return (
        <Carousel autoplay autoplaySpeed={10000} dots={false} slidesToShow={3} slidesToScroll={1}>
          {metrics.map(metric => (
            <MetricCard key={metric.key} metric={metric} period={period} />
          ))}
        </Carousel>
      );
    }

    return (
      <div className={styles.cardsGroup}>
        {metrics.map(metric => (
          <MetricCard key={metric.key} metric={metric} period={period} />
        ))}
      </div>
    );
  };

  return (
    <section className={styles.periodCard}>
      <div className={styles.periodTitle}>{title}</div>
      <Spin spinning={loading}>
        <div className={styles.metricsViewport}>{renderCards()}</div>
      </Spin>
    </section>
  );
};

const CenterTopCards: React.FC<CenterTopCardsProps> = ({ className }) => {
  const { data, loading } = useRequest(() => apiV2EnvMonitorDashboardCardOverviewPost({}));
  const metrics = useMemo(() => collectMetrics(data), [data]);

  return (
    <div className={`${styles.centerTopCards} ${className ?? ''}`}>
      <PeriodCard title="本月" period="month" metrics={metrics} loading={loading} />
      <PeriodCard title="本年" period="year" metrics={metrics} loading={loading} />
    </div>
  );
};

export default CenterTopCards;
